import React, { Fragment, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import star from "../assets/images/star.gif";
import * as userService from "../utils/userService";
import * as httpService from "../services/httpService";
import { Employee_Api_EndPoints } from "../global/end-points";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../components/common/loadingSpinner";

function MakingBreadCard() {
  const { t } = useTranslation();
  const didMount = useRef(false);
  const location = useLocation();

  const [isLoad, setIsLoad] = useState(false);
  const [balance, setBalance] = useState(0); // GET_BALANCE fallback
  const [fastCardBalance, setFastCardBalance] = useState(null); // null = not fetched yet
  const [availableWages, setAvailableWages] = useState(0);
  const [fee, setFee] = useState(3.95);

  const effectiveUserId =
    sessionStorage.getItem("OnBehalfUserId") || sessionStorage.getItem("UserId");

  const isOBO = !!sessionStorage.getItem("OnBehalfUserId");

  useEffect(() => {
    if (!didMount.current) {
      getBalance();
      getFee();

      if (location?.state?.typeOfTransfer === "loadFundsToCard") {
        getAvailableWages();
        // same flow as FastCardBalance
        if (!isOBO) getFastCardBalanceInfo();
      }
    }

    return () => {
      didMount.current = true;
    };
    // eslint-disable-next-line
  }, [location?.state?.typeOfTransfer]);

  const toNum = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const getBalance = () => {
    const uid = sessionStorage.getItem("UserId"); // balance belongs to real user
    if (uid !== null && uid !== undefined) {
      const body = { UserId: Number(uid) };
      httpService.Get(Employee_Api_EndPoints.GET_BALANCE, body).then(
        (resp) => {
          // ✅ Handle 500 errors and other error responses gracefully
          if (resp && resp.status === 200 && resp.data) {
            setBalance(toNum(resp.data?.cardBalance, 0));
          } else {
            // Error response (500, network error, etc.) - set default value
            setBalance(0);
          }
          setIsLoad(true);
        },
        (error) => {
          // Error handler - set default value
          console.error("Error fetching balance:", error);
          setBalance(0);
          setIsLoad(true);
        }
      );
    } else {
      setIsLoad(true);
      userService.ClearSession();
    }
  };

  const getAvailableWages = () => {
    if (effectiveUserId) {
      const body = { UserId: Number(effectiveUserId), LocationId: 1 };
      httpService.Get(Employee_Api_EndPoints.DOUGH_SUMMARY, body).then((resp) => {
        // ✅ Handle 500 errors and other error responses gracefully
        if (resp && resp.status === 200 && resp.data) {
          setAvailableWages(toNum(resp.data?.netPay, 0));
        } else {
          // Error response (500, network error, etc.) - set default value
          setAvailableWages(0);
        }
      });
    }
  };

  const getFee = () => {
    httpService.Get(Employee_Api_EndPoints.GET_ACH_FEE).then((resp) => {
      if (resp?.status === 200 && resp.data) {
        setFee(toNum(resp.data?.fee, 3.95));
      } else {
        setFee(3.95);
      }
    });
  };

  // ✅ FIXED: Proper promise chaining + use CallWithHeaders directly from service
  const getFastCardBalanceInfo = () => {
    if (!effectiveUserId) return;

    const body = { UserId: Number(effectiveUserId) };

    httpService.Get(Employee_Api_EndPoints.GET_USER_INFO, body).then(
      (resp) => {
        if (resp?.status === 200 && resp.data) {
          const phone =
            resp.data.PhoneNumber || resp.data.phoneNumber || resp.data.phone;

          if (!phone) return;

          httpService
            .CallWithHeaders(
              "get",
              Employee_Api_EndPoints.CARD_ACCOUNT_STATUS,
              { phoneNumber: phone },
              { SecretKey: "DoUgHPaYSeCrEKeY" }
            )
            .then((resp2) => {
              if (resp2?.status === 200 && resp2.data) {
                const adminNum =
                  resp2.data.transcardAdminNumber ||
                  resp2.data.adminNumber ||
                  resp2.data.AdminNumber;

                if (!adminNum) return;

                const fastBody = { AdminNumber: Number(adminNum) };

                httpService
                  .CallWithHeaders(
                    "post",
                    Employee_Api_EndPoints.FAST_CARD_BALANCE,
                    fastBody,
                    { SecretKey: "DoUgHPaYSeCrEKeY" }
                  )
                  .then((fastResp) => {
                    if (fastResp?.status === 200) {
                      const bal =
                        fastResp.data?.CurrentBalance ??
                        fastResp.data?.currentBalance ??
                        fastResp.data?.balance ??
                        0;

                      setFastCardBalance(toNum(bal, 0));
                    }
                  })
                  .catch(() => {
                    // ignore
                  });
              }
            })
            .catch(() => {
              // ignore
            });
        }
      },
      () => {
        // ignore
      }
    );
  };

  const transferredAmount =
    location?.state?.bakingValues?.transferredAmount ||
    location?.state?.bakingValues?.Amount ||
    location?.state?.bakingValues?.amount ||
    0;

  const transferred = toNum(transferredAmount, 0);
  const feeNum = toNum(fee, 3.95);
  const balNum = toNum(balance, 0);

  const loadedAmount = Math.max(0, transferred - feeNum);

  // If FAST_CARD_BALANCE fetched, use the larger of the FAST balance and
  // the inferred balance (GET_BALANCE + loadedAmount). This avoids showing
  // an undercount when the FAST_CARD_BALANCE endpoint returns a pre-load
  // value that hasn't been updated yet.
  const inferredCardBalance = balNum + loadedAmount;
  const currentCardBalance =
    fastCardBalance !== null
      ? Math.max(fastCardBalance, inferredCardBalance)
      : inferredCardBalance;

  // Compute updated available wages: use previousAvailableWages when provided
  // and deduct only the requested load amount (`transferred`). If not
  // provided, fall back to `availableWages` and also deduct only `transferred`.
  const prevAvailable = location?.state?.bakingValues?.previousAvailableWages;
  const baseAvailable = prevAvailable !== undefined && prevAvailable !== null
    ? toNum(prevAvailable, toNum(availableWages, 0))
    : toNum(availableWages, 0);
  const updatedAvailableWages = Math.max(0, baseAvailable - transferred);

  const isError = location?.state?.isError === true;
  const errorType = location?.state?.typeOfTransfer;

  return (
    <Fragment>
      {isLoad && (
        <div className="dashboard-container">
          {isError && errorType === "loadFundsToCard" ? (
            <div className="card-load-error-container">
              <p className="card-load-error-message">
                There was an issue loading funds onto your card.
              </p>
              <p className="card-load-error-message">Please try again.</p>
            </div>
          ) : location?.state?.typeOfTransfer === "assignCard" ? (
            <>
              <h1 className="card-load-success-title">
                Card Registered Successfully
              </h1>

              <div className="card-load-success-info">
                <p className="success-info-line">
                  Your card has been successfully registered.
                </p>
              </div>
            </>
          ) : location?.state?.typeOfTransfer === "loadFundsToCard" ? (
            <>
              <h1 className="card-load-success-title">
                {t("Card Load Successful!")}
              </h1>

              <div className="congrats-image-container">
                <img src={star} alt="" />
              </div>

              <div className="card-load-success-info">
                <p className="success-info-line">
                  Your card has been loaded with{" "}
                  <span className="success-amount">
                    ${transferred.toFixed(2)}
                  </span>.
                </p>

                {/* <p className="success-info-line">
                  Transaction fee of{" "}
                  <span className="success-amount">
                    ${feeNum.toFixed(2)}
                  </span>{" "}
                  has been deducted from your available balance.
                </p> */}

                <p className="success-info-line">
                  Your total available eligible wages{" "}
                  <span className="success-amount">
                    ${updatedAvailableWages.toFixed(2)}
                  </span>.
                </p>

                <p className="success-info-line">
                  Your total card balance is{" "}
                  <span className="success-amount">
                    ${toNum(currentCardBalance, 0).toFixed(2)}
                  </span>.
                </p>
              </div>
            </>
          ) : (
            <>
              <h1 className="available-dough-bigger">
                {t("CongoYourMakingBrd")}
              </h1>

              <div className="congrats-image-container">
                <img src={star} alt="" />
              </div>

              <h2 className="page-module-heading mb-15">
                You have transferred{" "}
                <span className="color-white">
                  $ {transferred.toFixed(2)}
                </span>{" "}
                to your Bank Account
              </h2>

              <h2 className="page-module-heading">
                {t("NewDhCrdBal")}{" "}
                <span className="color-white">
                  $ {toNum(currentCardBalance, 0).toFixed(2)}
                </span>
              </h2>
            </>
          )}
        </div>
      )}

      {!isLoad && <LoadingSpinner />}
    </Fragment>
  );
}

export default MakingBreadCard;
